// The full CMI record behind one row of the Course Progress report.
//
// The list endpoint deliberately returns only counts and previews for the heavy CMI
// fields — suspend_data and the raw cmi tree run to tens of KB per learner — so this
// drawer fetches the complete record for a single learner+course on demand.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Copy, Check, Download, ChevronRight } from 'lucide-react';
import { lmsAPI } from '../services/api';
import { downloadBlob } from '../utils/download';
import { useTableSort, SortTh } from './dashboard/useTableSort';
import type { SortValue } from './dashboard/useTableSort';
import { BlockLoader } from './loading';

interface ScormInteraction {
  index: number;
  id: string | null;
  type: string | null;
  response: string | null;
  result: string | null;
  weighting: string | null;
  latency: string | null;
  timestamp: string | null;
  description: string | null;
  correct_responses: (string | null)[];
  objectives: (string | null)[];
}

interface ScormObjective {
  index: number;
  id: string | null;
  status: string | null;
  completion_status: string | null;
  success_status: string | null;
  score_raw: string | number | null;
  score_scaled: string | number | null;
  progress_measure: string | number | null;
  description: string | null;
}

interface ScormScoreHistoryEntry {
  score_raw: string | number | null;
  score_scaled: string | number | null;
  lesson_status: string | null;
  recorded_at: string;
}

export interface ScormLearnerDetail {
  employee_code: string;
  employee_name: string;
  module_id: string;
  module_title: string;
  pass_score: number | null;
  points_reward: number | null;
  scorm_version: string | null;
  mastery_score: number | null;
  original_filename: string | null;

  lesson_status: string;
  completion_status: string | null;
  success_status: string | null;
  score_raw: string | number | null;
  score_min: string | number | null;
  score_max: string | number | null;
  score_scaled: string | number | null;
  progress_measure: string | number | null;
  total_time_seconds: number;
  attempts: number;
  points_awarded: number;
  points_awarded_at: string | null;
  entry: string | null;
  exit: string | null;
  lesson_location: string | null;
  suspend_data: string | null;
  suspend_data_bytes: number;

  started_at: string | null;
  completed_at: string | null;
  last_activity_at: string | null;

  interactions: ScormInteraction[];
  objectives: ScormObjective[];
  score_history: ScormScoreHistoryEntry[];
  cmi: unknown;
}

interface Props {
  moduleId: string;
  employeeCode: string;
  projectId?: number;
  /** Shown in the header while the request is still in flight. */
  fallbackName?: string;
  fallbackCourse?: string;
  onClose: () => void;
}

const fmtDateTime = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const fmtDuration = (seconds: number) => {
  if (!seconds || seconds <= 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.round(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const fmtBytes = (bytes: number) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const val = (v: string | number | null | undefined) =>
  v === null || v === undefined || v === '' ? '—' : String(v);

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900 break-words">{children}</dd>
    </div>
  );
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard?.writeText(text).then(
          () => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          },
          () => undefined,
        );
      }}
      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 border border-gray-200 rounded hover:bg-gray-50"
    >
      {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : label}
    </button>
  );
}

function Section({
  title,
  count,
  children,
  defaultOpen = true,
}: {
  title: string;
  count?: number;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="border-t border-gray-100 pt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 w-full text-left"
      >
        <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} />
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {count !== undefined && (
          <span className="text-xs text-gray-500">({count})</span>
        )}
      </button>
      {open && <div className="mt-3">{children}</div>}
    </section>
  );
}

// ── Sortable list tables ─────────────────────────────────────────────────────
// Each list table owns its sort hook, since they render conditionally inside
// collapsible Sections. '#' is the entry's own number, not data: it stays plain and
// keeps its value when the rows re-order.

// A CMI value as its cell shows it — compared numerically when it is a clean number.
const numOrText = (v: string | number | null | undefined): SortValue => {
  const s = v === null || v === undefined ? '' : String(v).trim();
  if (!s) return null;
  const num = Number(s);
  return Number.isFinite(num) ? num : s;
};

// Epoch ms, so timestamps sort chronologically.
const toMs = (iso: string | null): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
};

// cmi latency is a duration — ISO 8601 in SCORM 2004 ("PT1M30S"), HHHH:MM:SS.SS in 1.2.
// Compared as seconds so PT1M30S sorts after PT45S; anything unrecognised sorts as text.
const latencySeconds = (v: string | null): SortValue => {
  const s = (v ?? '').trim();
  const iso = /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/i.exec(s);
  if (iso && s.length > 1) {
    const [, y, mo, d, h, mi, sec] = iso.map((x) => Number(x ?? 0));
    return y * 31536000 + mo * 2592000 + d * 86400 + h * 3600 + mi * 60 + sec;
  }
  const hms = /^(\d+):(\d{1,2}):(\d{1,2}(?:\.\d+)?)$/.exec(s);
  if (hms) return Number(hms[1]) * 3600 + Number(hms[2]) * 60 + Number(hms[3]);
  return s || null;
};

function ScoreHistoryTable({ history }: { history: ScormScoreHistoryEntry[] }) {
  // Pair each entry with its position first, so '#' (and the row key) stay with it once sorted.
  const numbered = useMemo(() => history.map((h, i) => ({ h, i })), [history]);
  const sort = useTableSort(numbered, {
    score: ({ h }) => numOrText(h.score_raw),
    scaled: ({ h }) => numOrText(h.score_scaled),
    status: ({ h }) => h.lesson_status,
    recorded: ({ h }) => toMs(h.recorded_at),
  });
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50/70 border-b border-gray-100 text-xs text-gray-600">
            <th className="px-3 py-2 text-left font-semibold">#</th>
            <SortTh label="Score" sortKey="score" sort={sort} className="px-3 py-2 text-left font-semibold" />
            <SortTh label="Scaled" sortKey="scaled" sort={sort} className="px-3 py-2 text-left font-semibold" />
            <SortTh label="Status" sortKey="status" sort={sort} className="px-3 py-2 text-left font-semibold" />
            <SortTh label="Recorded" sortKey="recorded" sort={sort} className="px-3 py-2 text-left font-semibold" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sort.sorted.map(({ h, i }) => (
            <tr key={`${h.recorded_at}-${i}`}>
              <td className="px-3 py-2 text-gray-500">{i + 1}</td>
              <td className="px-3 py-2 text-gray-900">{val(h.score_raw)}</td>
              <td className="px-3 py-2 text-gray-700">{val(h.score_scaled)}</td>
              <td className="px-3 py-2 text-gray-700">{val(h.lesson_status)}</td>
              <td className="px-3 py-2 text-gray-500">{fmtDateTime(h.recorded_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InteractionsTable({ interactions }: { interactions: ScormInteraction[] }) {
  const sort = useTableSort(interactions, {
    id: (it) => it.id,
    type: (it) => it.type,
    response: (it) => numOrText(it.response),
    correct: (it) => numOrText(it.correct_responses.filter(Boolean).join(', ')),
    result: (it) => numOrText(it.result),
    latency: (it) => latencySeconds(it.latency),
  });
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50/70 border-b border-gray-100 text-xs text-gray-600">
            <th className="px-3 py-2 text-left font-semibold">#</th>
            <SortTh label="ID" sortKey="id" sort={sort} className="px-3 py-2 text-left font-semibold" />
            <SortTh label="Type" sortKey="type" sort={sort} className="px-3 py-2 text-left font-semibold" />
            <SortTh label="Response" sortKey="response" sort={sort} className="px-3 py-2 text-left font-semibold" />
            <SortTh label="Correct answer" sortKey="correct" sort={sort} className="px-3 py-2 text-left font-semibold" />
            <SortTh label="Result" sortKey="result" sort={sort} className="px-3 py-2 text-left font-semibold" />
            <SortTh label="Latency" sortKey="latency" sort={sort} className="px-3 py-2 text-left font-semibold" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sort.sorted.map((it) => (
            <tr key={it.index} className="align-top">
              <td className="px-3 py-2 text-gray-500">{it.index + 1}</td>
              <td className="px-3 py-2 text-gray-900 max-w-[12rem] break-words">
                {val(it.id)}
                {it.description && (
                  <span className="block text-xs text-gray-400">{it.description}</span>
                )}
              </td>
              <td className="px-3 py-2 text-gray-700">{val(it.type)}</td>
              <td className="px-3 py-2 text-gray-700 max-w-[14rem] break-words">
                {val(it.response)}
              </td>
              <td className="px-3 py-2 text-gray-500 max-w-[14rem] break-words">
                {it.correct_responses.filter(Boolean).length > 0
                  ? it.correct_responses.filter(Boolean).join(', ')
                  : '—'}
              </td>
              <td className="px-3 py-2">
                <span
                  className={
                    (it.result || '').toLowerCase() === 'correct'
                      ? 'text-green-700 font-medium'
                      : (it.result || '').toLowerCase() === 'incorrect' || (it.result || '').toLowerCase() === 'wrong'
                        ? 'text-red-700 font-medium'
                        : 'text-gray-600'
                  }
                >
                  {val(it.result)}
                </span>
              </td>
              <td className="px-3 py-2 text-gray-500">{val(it.latency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ObjectivesTable({ objectives }: { objectives: ScormObjective[] }) {
  const sort = useTableSort(objectives, {
    id: (ob) => ob.id,
    // The status text as shown: "<status> / <success status>".
    status: (ob) => [ob.status ?? ob.completion_status, ob.success_status].filter(Boolean).join(' / '),
    score: (ob) => numOrText(ob.score_raw),
    progress: (ob) => numOrText(ob.progress_measure),
  });
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50/70 border-b border-gray-100 text-xs text-gray-600">
            <th className="px-3 py-2 text-left font-semibold">#</th>
            <SortTh label="ID" sortKey="id" sort={sort} className="px-3 py-2 text-left font-semibold" />
            <SortTh label="Status" sortKey="status" sort={sort} className="px-3 py-2 text-left font-semibold" />
            <SortTh label="Score" sortKey="score" sort={sort} className="px-3 py-2 text-left font-semibold" />
            <SortTh label="Progress" sortKey="progress" sort={sort} className="px-3 py-2 text-left font-semibold" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sort.sorted.map((ob) => (
            <tr key={ob.index}>
              <td className="px-3 py-2 text-gray-500">{ob.index + 1}</td>
              <td className="px-3 py-2 text-gray-900 max-w-[16rem] break-words">
                {val(ob.id)}
                {ob.description && (
                  <span className="block text-xs text-gray-400">{ob.description}</span>
                )}
              </td>
              <td className="px-3 py-2 text-gray-700">
                {val(ob.status ?? ob.completion_status)}
                {ob.success_status && (
                  <span className="text-gray-400"> / {ob.success_status}</span>
                )}
              </td>
              <td className="px-3 py-2 text-gray-700">
                {val(ob.score_raw)}
                {ob.score_scaled !== null && ob.score_scaled !== undefined && ob.score_scaled !== '' && (
                  <span className="text-gray-400"> ({ob.score_scaled})</span>
                )}
              </td>
              <td className="px-3 py-2 text-gray-700">
                {ob.progress_measure !== null && ob.progress_measure !== undefined && ob.progress_measure !== ''
                  ? `${Math.round(Number(ob.progress_measure) * 100)}%`
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ScormLearnerDetailDrawer({
  moduleId,
  employeeCode,
  projectId,
  fallbackName,
  fallbackCourse,
  onClose,
}: Props) {
  const [data, setData] = useState<ScormLearnerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSuspend, setShowSuspend] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const resp = await lmsAPI.getScormProgressDetail({ moduleId, employeeCode, projectId });
        if (cancelled) return;
        setData(resp.data?.data ?? null);
      } catch (err) {
        console.error('Error loading SCORM learner detail:', err);
        if (!cancelled) setError('Could not load the full record for this learner.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [moduleId, employeeCode, projectId]);

  // Escape closes, matching the other admin drawers.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleDownloadJson = useCallback(() => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    downloadBlob(`scorm-${data.employee_code}-${data.module_id}.json`, blob);
  }, [data]);

  const heading = data?.employee_name || fallbackName || employeeCode;
  const course = data?.module_title || fallbackCourse || '';

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-3xl h-full bg-white shadow-xl overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 px-6 py-4 bg-white border-b border-gray-100">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 truncate">{heading}</h2>
            <p className="text-sm text-gray-500 truncate">
              {employeeCode}
              {course && <> · {course}</>}
              {data?.scorm_version && (
                <span className="ml-2 inline-block px-1.5 py-0.5 text-[11px] bg-gray-100 text-gray-600 rounded">
                  SCORM {data.scorm_version}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {data && (
              <button
                type="button"
                onClick={handleDownloadJson}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <Download className="w-3.5 h-3.5" />
                JSON
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          {loading ? (
            <BlockLoader label="Loading full record…" className="py-16" />
          ) : error ? (
            <div className="py-16 text-center text-sm text-red-600">{error}</div>
          ) : !data ? (
            <div className="py-16 text-center text-sm text-gray-500">No record found.</div>
          ) : (
            <>
              {/* Status + score */}
              <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Field label="Status">{val(data.lesson_status)}</Field>
                <Field label="Completion">{val(data.completion_status)}</Field>
                <Field label="Success">{val(data.success_status)}</Field>
                <Field label="Progress measure">
                  {data.progress_measure !== null && data.progress_measure !== undefined && data.progress_measure !== ''
                    ? `${Math.round(Number(data.progress_measure) * 100)}%`
                    : '—'}
                </Field>

                <Field label="Score (raw)">{val(data.score_raw)}</Field>
                <Field label="Score range">
                  {data.score_min === null && data.score_max === null
                    ? '—'
                    : `${val(data.score_min)} – ${val(data.score_max)}`}
                </Field>
                <Field label="Score (scaled)">{val(data.score_scaled)}</Field>
                <Field label="Pass / mastery">
                  {data.mastery_score ?? data.pass_score ?? '—'}
                </Field>

                <Field label="Attempts">{data.attempts || '—'}</Field>
                <Field label="Time spent">{fmtDuration(data.total_time_seconds)}</Field>
                <Field label="Points awarded">
                  {data.points_awarded || 0}
                  {data.points_reward ? <span className="text-gray-400"> / {data.points_reward}</span> : null}
                </Field>
                <Field label="Points awarded at">{fmtDateTime(data.points_awarded_at)}</Field>

                <Field label="Started">{fmtDateTime(data.started_at)}</Field>
                <Field label="Completed">{fmtDateTime(data.completed_at)}</Field>
                <Field label="Last activity">{fmtDateTime(data.last_activity_at)}</Field>
                <Field label="Entry / exit">
                  {val(data.entry)} / {val(data.exit)}
                </Field>
              </dl>

              {/* Bookmark */}
              <Section title="Bookmark & resume state">
                <dl className="grid grid-cols-1 gap-4">
                  <Field label="Lesson location (the stage the learner is on)">
                    {data.lesson_location ? (
                      <span className="inline-flex items-center gap-2">
                        <code className="px-1.5 py-0.5 bg-gray-50 border border-gray-200 rounded text-xs">
                          {data.lesson_location}
                        </code>
                        <CopyButton text={data.lesson_location} />
                      </span>
                    ) : (
                      <span className="text-gray-400">
                        Not reported — this package tracks position inside suspend_data instead.
                      </span>
                    )}
                  </Field>
                  <Field label="Suspend data">
                    {data.suspend_data ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">{fmtBytes(data.suspend_data_bytes)}</span>
                          <button
                            type="button"
                            onClick={() => setShowSuspend((v) => !v)}
                            className="text-xs text-primary-600 hover:underline"
                          >
                            {showSuspend ? 'Hide' : 'Show raw'}
                          </button>
                          <CopyButton text={data.suspend_data} />
                        </div>
                        {showSuspend && (
                          <pre className="max-h-56 overflow-auto p-3 bg-gray-50 border border-gray-200 rounded text-[11px] whitespace-pre-wrap break-all">
                            {data.suspend_data}
                          </pre>
                        )}
                        <p className="text-xs text-gray-400">
                          An opaque, course-specific blob. It is what makes resume work; it is not
                          meaningfully readable outside the authoring tool that wrote it.
                        </p>
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </Field>
                </dl>
              </Section>

              {/* Score history */}
              <Section title="Scored submissions" count={data.score_history.length}>
                {data.score_history.length === 0 ? (
                  <p className="text-sm text-gray-500">No scored submission recorded yet.</p>
                ) : (
                  <ScoreHistoryTable history={data.score_history} />
                )}
              </Section>

              {/* Interactions */}
              <Section title="Interactions" count={data.interactions.length}>
                {data.interactions.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    This package does not report per-question interactions.
                  </p>
                ) : (
                  <InteractionsTable interactions={data.interactions} />
                )}
              </Section>

              {/* Objectives */}
              <Section title="Objectives" count={data.objectives.length}>
                {data.objectives.length === 0 ? (
                  <p className="text-sm text-gray-500">This package does not report objectives.</p>
                ) : (
                  <ObjectivesTable objectives={data.objectives} />
                )}
              </Section>

              {/* Raw CMI */}
              <Section title="Raw CMI tree" defaultOpen={false}>
                <div className="space-y-2">
                  <CopyButton text={JSON.stringify(data.cmi, null, 2)} label="Copy JSON" />
                  <pre className="max-h-96 overflow-auto p-3 bg-gray-50 border border-gray-200 rounded text-[11px]">
                    {data.cmi ? JSON.stringify(data.cmi, null, 2) : 'No CMI recorded yet.'}
                  </pre>
                </div>
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
